import { supabase } from '../supabase';
import type {
  EmailAutomation,
  EmailAutomationRun,
  EmailAutomationStep,
  TriggerKind,
} from '../../types/automazioni.types';
import { configPartenza, type Partenza } from './partenzaSequenza';

/**
 * Accesso ai dati delle sequenze automatiche.
 *
 * Come in emailService: le tabelle email_* non sono ancora in
 * database.types.ts, quindi il client va forzato una volta sola qui invece di
 * ripetere un cast a ogni query.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export interface EtichettaScelta {
  id: string;
  name: string;
  color: string | null;
  /** Quanti contatti della lista possono ricevere adesso. */
  possono_ricevere: number;
  /** Quanti ricevono una sequenza di servizio: anche chi non ha accettato
   *  le promozioni, non chi si e' disiscritto. */
  ricevono_servizio: number;
}

export interface DisegnoSequenza {
  automazione: EmailAutomation;
  passi: EmailAutomationStep[];
}

/** Quanti contatti ci sono dentro adesso e come sono finiti gli altri. */
export interface NumeriSequenza {
  in_corso: number;
  finite: number;
  ferme: number;
}

export const automationService = {
  async elenco(): Promise<EmailAutomation[]> {
    const { data, error } = await db
      .from('email_automations')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as EmailAutomation[];
  },

  async dettaglio(id: string): Promise<DisegnoSequenza> {
    const [automazione, passi] = await Promise.all([
      db.from('email_automations').select('*').eq('id', id).single(),
      db.from('email_automation_steps').select('*').eq('automation_id', id),
    ]);
    if (automazione.error) throw automazione.error;
    if (passi.error) throw passi.error;
    return {
      automazione: automazione.data as EmailAutomation,
      passi: (passi.data || []) as EmailAutomationStep[],
    };
  },

  async crea(nome: string, partenza: Partenza, servizio: boolean): Promise<EmailAutomation> {
    const { data, error } = await db
      .from('email_automations')
      .insert({
        name: nome,
        trigger_kind: partenza.trigger,
        trigger_config: configPartenza(partenza),
        servizio,
      })
      .select('*')
      .single();
    if (error) throw error;
    return data as EmailAutomation;
  },

  async aggiorna(
    id: string,
    campi: Partial<{
      name: string;
      description: string | null;
      trigger_kind: TriggerKind;
      trigger_config: Record<string, unknown>;
      ripetibile: boolean;
      servizio: boolean;
    }>
  ): Promise<void> {
    const { error } = await db.from('email_automations').update(campi).eq('id', id);
    if (error) throw error;
  },

  /**
   * Salva il disegno intero: tutte le caselle e tutte le frecce in una volta.
   * Lo fa il database in un colpo solo, perche' a meta' strada il percorso
   * avrebbe frecce che finiscono nel vuoto.
   */
  async salvaDisegno(
    id: string,
    firstStepId: string | null,
    passi: EmailAutomationStep[]
  ): Promise<void> {
    const { error } = await db.rpc('email_automazione_salva', {
      p_automation_id: id,
      p_first_step_id: firstStepId,
      p_steps: passi.map((p) => ({
        id: p.id,
        kind: p.kind,
        label: p.label,
        config: p.config,
        next_id: p.next_id,
        next_alt_id: p.next_alt_id,
        pos_x: p.pos_x,
        pos_y: p.pos_y,
      })),
    });
    if (error) throw error;
  },

  /** Restituisce l'elenco dei problemi. Vuoto = si puo' accendere. */
  async valida(id: string): Promise<string[]> {
    const { data, error } = await db.rpc('email_automazione_valida', { p_automation_id: id });
    if (error) throw error;
    return (data || []) as string[];
  },

  /** Accende. Se torna una lista non vuota, non si e' acceso niente. */
  async accendi(id: string): Promise<string[]> {
    const { data, error } = await db.rpc('email_automazione_accendi', { p_automation_id: id });
    if (error) throw error;
    return (data || []) as string[];
  },

  async metti_in_pausa(id: string): Promise<void> {
    const { error } = await db
      .from('email_automations')
      .update({ status: 'sospesa' })
      .eq('id', id);
    if (error) throw error;
  },

  async elimina(id: string): Promise<void> {
    const { error } = await db.from('email_automations').delete().eq('id', id);
    if (error) throw error;
  },

  /** Fa entrare a mano i contatti scelti. Torna quanti sono entrati davvero. */
  async avviaSuContatti(id: string, contactIds: string[]): Promise<number> {
    const { data, error } = await db.rpc('email_automazione_avvia_manuale', {
      p_automation_id: id,
      p_contact_ids: contactIds,
    });
    if (error) throw error;
    return (data as number) ?? 0;
  },

  /** Conta i contatti, non le azioni "ogni volta" eseguite per loro: le
   *  percorrenze figlie (parent_run_id) restano fuori. */
  async numeri(id: string): Promise<NumeriSequenza> {
    const { data, error } = await db
      .from('email_automation_runs')
      .select('state')
      .eq('automation_id', id)
      .is('parent_run_id', null);
    if (error) throw error;

    const righe = (data || []) as { state: EmailAutomationRun['state'] }[];
    return {
      in_corso: righe.filter((r) => r.state === 'in_corso').length,
      finite: righe.filter((r) => r.state === 'finita').length,
      ferme: righe.filter((r) => r.state === 'ferma').length,
    };
  },

  /** Le liste, con quanti possono ricevere: serve a dire quanti entrano
   *  accendendo una sequenza che parte da tutta la lista. */
  async etichette(): Promise<EtichettaScelta[]> {
    const { data, error } = await db
      .from('email_liste')
      .select('id, name, color, possono_ricevere, ricevono_servizio')
      .order('name');
    if (error) throw error;
    return (data || []) as EtichettaScelta[];
  },
};
